// src/pages/ServiceProviderDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getListingsByUserId } from '../services/ListingsService';
import { getUserStripeAccount } from '../services/UserRolesService';
import { redirectToSubscriptionCheckout, cancelProviderSubscription } from '../services/SubscriptionService';
import { doc, getDoc, collection, query, where, getDocs, orderBy, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import StripeConnect from '../components/StripeConnect';
import './ServiceProviderDashboard.css';

function ServiceProviderDashboard() {
  const { currentUser, isApprovedProvider } = useAuth();
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('purchaseDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('all');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [showStripeConnect, setShowStripeConnect] = useState(false);
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [selectedListingForMembers, setSelectedListingForMembers] = useState(null);
  const [showMembersList, setShowMembersList] = useState(false);
  const [companyProfile, setCompanyProfile] = useState({
    businessName: '',
    headline: '',
    description: '',
    website: '',
    phone: '',
    contactEmail: '',
    instagram: '',
    facebook: '',
    linkedin: ''
  });
  const [profileStatus, setProfileStatus] = useState({ saving: false, error: null, lastSaved: null });
  const [isProfileDirty, setIsProfileDirty] = useState(false);

  const handleProfileInputChange = (field, value) => {
    setCompanyProfile(prev => ({
      ...prev,
      [field]: value
    }));
    setIsProfileDirty(true);
  };

  const handleSaveCompanyProfile = async () => {
    if (!currentUser) return;
    setProfileStatus(prev => ({ ...prev, saving: true, error: null }));
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        companyProfile: {
          ...companyProfile,
          updatedAt: serverTimestamp()
        }
      });
      setProfileStatus({ saving: false, error: null, lastSaved: new Date() });
      setIsProfileDirty(false);
    } catch (err) {
      console.error('Error saving company profile:', err);
      setProfileStatus(prev => ({
        saving: false,
        error: 'Failed to save profile. Please try again.',
        lastSaved: prev.lastSaved
      }));
    }
  };
  
  // Check Stripe Connect status and subscription status
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!currentUser) return;
      
      try {
        const stripeInfo = await getUserStripeAccount(currentUser.uid);
        setStripeConnectStatus(stripeInfo);
        
        // Get user subscription status
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserSubscriptionStatus({
            status: userData.subscriptionStatus || 'none',
            type: userData.subscriptionType || null,
            cancelAtPeriodEnd: userData.subscriptionCancelAtPeriodEnd || false,
            currentPeriodEnd: userData.subscriptionCurrentPeriodEnd || null
          });
          
          if (userData) {
            const profileData = userData.companyProfile || {};
            setCompanyProfile({
              businessName: profileData.businessName || userData.businessName || '',
              headline: profileData.headline || '',
              description: profileData.description || '',
              website: profileData.website || '',
              phone: profileData.phone || '',
              contactEmail: profileData.contactEmail || userData.email || '',
              instagram: profileData.instagram || '',
              facebook: profileData.facebook || '',
              linkedin: profileData.linkedin || ''
            });
            setIsProfileDirty(false);
          }
        }
        
        // Show Stripe Connect component if not completed
        if (!stripeInfo || stripeInfo.onboardingStatus !== 'completed') {
          setShowStripeConnect(true);
        }
      } catch (err) {
        console.error("Error checking user status:", err);
      }
    };
    
    checkUserStatus();
  }, [currentUser]);
  
  // Fetch user's listings and sales data
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !isApprovedProvider) {
        setLoading(false);
        return;
      }

      try {
        console.log("ServiceProviderDashboard: Fetching data for user:", currentUser.uid);
        
        // First, fetch all listings created by this user
        const userListings = await getListingsByUserId(currentUser.uid);
        console.log("ServiceProviderDashboard: Found user listings:", userListings.length);
        
        
        // Save a map of listingId -> listing details for quick lookup
        const listingsMap = {};
        userListings.forEach(listing => {
          listingsMap[listing.id] = listing;
        });
        
        // Fetch payments for this provider's listings
        // We need to query by providerId since the security rules don't allow querying all payments
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('providerId', '==', currentUser.uid),
          where('status', '==', 'succeeded'),
          orderBy('createdAt', 'desc')
        );
        
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const providerPayments = paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        
        console.log("ServiceProviderDashboard: Found provider payments:", providerPayments.length);
        
        const allSalesData = [];
        let revenue = 0;
        
        // Process each payment
        for (const payment of providerPayments) {
          try {
            // Check if listing exists in our map (if not, it's deleted)
            const isDeleted = !listingsMap[payment.listingId];
            console.log(`Payment ${payment.id} for listing ${payment.listingId} - isDeleted: ${isDeleted}`);
            
            // Get listing details (might be deleted)
            const listingDetails = listingsMap[payment.listingId] || {
              id: payment.listingId,
              title: "Deleted Listing",
              category: "unavailable",
              imageUrl: null
            };
            
            // Get purchaser details
            let purchaserData = { 
              displayName: 'Anonymous User', 
              email: 'No email available'
            };
            
            try {
              const purchaserRef = doc(db, 'users', payment.userId);
              const purchaserDoc = await getDoc(purchaserRef);
              
              if (purchaserDoc.exists()) {
                purchaserData = purchaserDoc.data();
              }
            } catch (err) {
              console.error(`Error fetching purchaser data for payment ${payment.id}:`, err);
            }
            
            // Calculate provider's share (amount after platform fee)
            const qty = payment.quantity || 1;
            const providerAmount = payment.providerAmount || (payment.amount * 0.8);
            revenue += providerAmount;
            
            // Add the payment with all details to salesData
            allSalesData.push({
              paymentId: payment.id,
              listingId: payment.listingId,
              listingTitle: isDeleted ? "Deleted Listing" : listingDetails.title,
              listingImage: isDeleted ? null : listingDetails.imageUrl,
              purchaserId: payment.userId,
              purchaserName: purchaserData.displayName || 'Anonymous User',
              purchaserEmail: purchaserData.email || 'No email available',
              purchaseDate: payment.createdAt || new Date(),
              totalAmount: payment.amount,
              quantity: qty,
              providerAmount: providerAmount,
              platformFee: payment.platformFee || (payment.amount * 0.2),
              category: isDeleted ? "unavailable" : listingDetails.category,
              location: isDeleted ? "" : listingDetails.location,
              isVirtual: isDeleted ? false : listingDetails.isVirtual,
              isDeleted: isDeleted
            });
          } catch (err) {
            console.error(`Error processing payment ${payment.id}:`, err);
          }
        }
        
        console.log("Final processed sales data:", allSalesData);
        console.log("Sales with deleted listings:", allSalesData.filter(sale => sale.isDeleted).length);
        
        setSalesData(allSalesData);
        setTotalRevenue(revenue);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load your dashboard data. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, isApprovedProvider]);

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time for display
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Filter sales data based on search term and time frame
  const filterSalesData = () => {
    let filtered = [...salesData];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(sale => 
        sale.listingTitle.toLowerCase().includes(term) || 
        sale.purchaserName.toLowerCase().includes(term) || 
        sale.purchaserEmail.toLowerCase().includes(term)
      );
    }
    
    // Filter by time frame
    if (selectedTimeFrame !== 'all') {
      const now = new Date();
      let cutoffDate = new Date();
      
      switch (selectedTimeFrame) {
        case 'today':
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          cutoffDate = new Date(0); // Beginning of time
      }
      
      filtered = filtered.filter(sale => sale.purchaseDate >= cutoffDate);
    }
    
    // Sort data
    filtered.sort((a, b) => {
      let fieldA = a[sortField];
      let fieldB = b[sortField];
      
      if (fieldA instanceof Date && fieldB instanceof Date) {
        return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
      }
      
      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc' 
          ? fieldA.localeCompare(fieldB) 
          : fieldB.localeCompare(fieldA);
      }
      
      if (typeof fieldA === 'number' && typeof fieldB === 'number') {
        return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
      }
      
      return 0;
    });
    
    return filtered;
  };
  
  // Handle sort change
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, set to desc by default
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Get total filtered revenue
  const getFilteredRevenue = () => {
    return filterSalesData().reduce((sum, sale) => sum + sale.providerAmount, 0).toFixed(2);
  };

  // Get members for a specific listing
  const getMembersForListing = (listingId) => {
    return salesData
      .filter(sale => sale.listingId === listingId)
      .map(sale => ({
        id: sale.paymentId,
        name: sale.purchaserName,
        email: sale.purchaserEmail,
        purchaseDate: sale.purchaseDate,
        amount: sale.totalAmount,
        quantity: sale.quantity || 1
      }))
      .sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
  };

  const getMemberCountForListing = (listingId) => {
    return salesData
      .filter(sale => sale.listingId === listingId)
      .reduce((sum, sale) => sum + (sale.quantity || 1), 0);
  };

  // Show members list for a listing
  const showMembersForListing = (listingId, listingTitle) => {
    setSelectedListingForMembers({ id: listingId, title: listingTitle });
    setShowMembersList(true);
  };

  // Close members list
  const closeMembersList = () => {
    setShowMembersList(false);
    setSelectedListingForMembers(null);
  };

  // Handle subscription upgrade
  const handleSubscriptionUpgrade = async () => {
    if (!currentUser) return;
    
    setSubscriptionLoading(true);
    try {
      await redirectToSubscriptionCheckout(currentUser.uid);
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      alert('Failed to start subscription upgrade. Please try again.');
    }
    setSubscriptionLoading(false);
  };

  // Handle subscription cancellation
  const handleSubscriptionCancel = async () => {
    if (!currentUser) return;
    
    if (!window.confirm('Are you sure you want to cancel your premium subscription? You will revert to 80% earnings at the end of your billing period.')) {
      return;
    }
    
    setSubscriptionLoading(true);
    try {
      await cancelProviderSubscription(currentUser.uid);
      // Refresh user status
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserSubscriptionStatus({
          status: userData.subscriptionStatus || 'none',
          type: userData.subscriptionType || null,
          cancelAtPeriodEnd: userData.subscriptionCancelAtPeriodEnd || false,
          currentPeriodEnd: userData.subscriptionCurrentPeriodEnd || null
        });
      }
      alert('Subscription will be canceled at the end of your billing period.');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    }
    setSubscriptionLoading(false);
  };

  if (loading) {
    return <div className="dashboard-loading">Loading your sales data...</div>;
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  if (!isApprovedProvider) {
    return (
      <div className="dashboard-not-provider">
        <div className="no-access-message">
          <h2>Provider Access Required</h2>
          <p>This dashboard is only available for approved service providers.</p>
          <Link to="/become-provider" className="action-button">
            Become a Provider
          </Link>
        </div>
      </div>
    );
  }

  const filteredSales = filterSalesData();

  // Show Stripe Connect setup if needed
  if (showStripeConnect) {
    return (
      <div className="provider-dashboard">
        <div className="dashboard-header">
          <h1>Payment Setup Required</h1>
          <p>Complete your payment setup to start receiving payments</p>
        </div>
        
        <StripeConnect />
        
        <div className="dashboard-footer" style={{ marginTop: '2rem' }}>
          <button 
            onClick={() => setShowStripeConnect(false)} 
            className="dashboard-link"
            style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Skip for now (you won't receive payments until setup is complete)
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-dashboard">
      <div className="dashboard-header">
        <h1>Sales Dashboard</h1>
        <p>Track your sales and customer information</p>
      </div>
      
      {/* Stripe Connect Status Banner */}
      {stripeConnectStatus && stripeConnectStatus.onboardingStatus !== 'completed' && (
        <div className="stripe-status-banner">
          <div className="stripe-status-content">
            <div className="stripe-status-icon">⚠️</div>
            <div className="stripe-status-text">
              <h3>Payment Setup Incomplete</h3>
              <p>You need to complete your payment setup to receive payments from your sales.</p>
            </div>
            <button 
              onClick={() => setShowStripeConnect(true)}
              className="stripe-setup-button"
            >
              Complete Setup
            </button>
          </div>
        </div>
      )}

      {/* Premium Subscription Banner */}
      {userSubscriptionStatus && userSubscriptionStatus.status !== 'active' && (
        <div className="premium-upgrade-banner">
          <div className="premium-upgrade-content">
            <div className="premium-upgrade-icon">⭐</div>
            <div className="premium-upgrade-text">
              <h3>Upgrade to Premium Provider</h3>
              <p>Get 90% of your earnings instead of 80% for just $333 CAD + tax per year (~$28/month)!</p>
              <div className="premium-benefits">
                <span>✓ 90% earnings rate</span>
                <span>✓ Higher profit margins</span>
                <span>✓ Cancel anytime</span>
              </div>
            </div>
            <button 
              onClick={handleSubscriptionUpgrade}
              disabled={subscriptionLoading}
              className="premium-upgrade-button"
            >
              {subscriptionLoading ? 'Loading...' : 'Upgrade Now'}
            </button>
          </div>
        </div>
      )}

      {/* Active Premium Subscription Status */}
      {userSubscriptionStatus && userSubscriptionStatus.status === 'active' && (
        <div className="premium-status-banner active">
          <div className="premium-status-content">
            <div className="premium-status-icon">⭐</div>
            <div className="premium-status-text">
              <h3>Premium Provider Active</h3>
              <p>You're earning 90% from each sale!</p>
              {userSubscriptionStatus.cancelAtPeriodEnd && (
                <p className="cancel-notice">Your subscription will end on {userSubscriptionStatus.currentPeriodEnd ? new Date(userSubscriptionStatus.currentPeriodEnd.seconds * 1000).toLocaleDateString() : 'your next billing date'}.</p>
              )}
            </div>
            {!userSubscriptionStatus.cancelAtPeriodEnd && (
              <button 
                onClick={handleSubscriptionCancel}
                disabled={subscriptionLoading}
                className="premium-cancel-button"
              >
                {subscriptionLoading ? 'Loading...' : 'Cancel Subscription'}
              </button>
            )}
          </div>
        </div>
      )}

      <section className="profile-card">
        <div className="profile-card-header">
          <div>
            <h2>Company Profile</h2>
            <p>Information shown to families on your listings.</p>
          </div>
          {profileStatus.lastSaved && (
            <span className="profile-saved-at">
              Last saved {profileStatus.lastSaved.toLocaleString()}
            </span>
          )}
        </div>
        {profileStatus.error && (
          <div className="profile-error">{profileStatus.error}</div>
        )}
        <div className="profile-card-grid">
          <div className="profile-field">
            <label htmlFor="businessName">Business Name</label>
            <input
              id="businessName"
              type="text"
              value={companyProfile.businessName}
              onChange={(e) => handleProfileInputChange('businessName', e.target.value)}
              placeholder="e.g., Bright Minds Learning Co."
            />
          </div>
          <div className="profile-field">
            <label htmlFor="headline">Headline</label>
            <input
              id="headline"
              type="text"
              value={companyProfile.headline}
              onChange={(e) => handleProfileInputChange('headline', e.target.value)}
              placeholder="Short tagline for your services"
            />
          </div>
          <div className="profile-field profile-field-full">
            <label htmlFor="description">Company Description</label>
            <textarea
              id="description"
              rows="4"
              value={companyProfile.description}
              onChange={(e) => handleProfileInputChange('description', e.target.value)}
              placeholder="Share your mission, experience, and unique value."
            />
          </div>
          <div className="profile-field">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="url"
              value={companyProfile.website}
              onChange={(e) => handleProfileInputChange('website', e.target.value)}
              placeholder="https://yourwebsite.com"
            />
          </div>
          <div className="profile-field">
            <label htmlFor="contactEmail">Contact Email</label>
            <input
              id="contactEmail"
              type="email"
              value={companyProfile.contactEmail}
              onChange={(e) => handleProfileInputChange('contactEmail', e.target.value)}
              placeholder="hello@yourcompany.com"
            />
          </div>
          <div className="profile-field">
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              type="tel"
              value={companyProfile.phone}
              onChange={(e) => handleProfileInputChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>
          <div className="profile-field">
            <label htmlFor="instagram">Instagram</label>
            <input
              id="instagram"
              type="url"
              value={companyProfile.instagram}
              onChange={(e) => handleProfileInputChange('instagram', e.target.value)}
              placeholder="https://instagram.com/yourhandle"
            />
          </div>
          <div className="profile-field">
            <label htmlFor="facebook">Facebook</label>
            <input
              id="facebook"
              type="url"
              value={companyProfile.facebook}
              onChange={(e) => handleProfileInputChange('facebook', e.target.value)}
              placeholder="https://facebook.com/yourpage"
            />
          </div>
          <div className="profile-field">
            <label htmlFor="linkedin">LinkedIn</label>
            <input
              id="linkedin"
              type="url"
              value={companyProfile.linkedin}
              onChange={(e) => handleProfileInputChange('linkedin', e.target.value)}
              placeholder="https://linkedin.com/company/yourcompany"
            />
          </div>
        </div>
        <div className="profile-card-actions">
          <button
            type="button"
            onClick={handleSaveCompanyProfile}
            disabled={profileStatus.saving || !isProfileDirty}
          >
            {profileStatus.saving ? 'Saving...' : 'Save Company Profile'}
          </button>
          {!isProfileDirty && !profileStatus.saving && (
            <span className="profile-hint">Make a change to enable saving.</span>
          )}
        </div>
      </section>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-card-inner">
            <div className="stat-icon sales-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div className="stat-content">
              <h3>Your Earnings</h3>
              <div className="stat-value">${totalRevenue.toFixed(2)}</div>
              <div className="stat-label">After platform fees</div>
            </div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-card-inner">
            <div className="stat-icon customers-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Total Customers</h3>
              <div className="stat-value">{new Set(salesData.map(sale => sale.purchaserId)).size}</div>
              <div className="stat-label">Unique buyers</div>
            </div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-card-inner">
            <div className="stat-icon orders-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Total Sales</h3>
              <div className="stat-value">{salesData.length}</div>
              <div className="stat-label">Completed orders</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="dashboard-filters">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search by title or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="timeframe-filter">
          <select 
            value={selectedTimeFrame} 
            onChange={(e) => setSelectedTimeFrame(e.target.value)}
            className="timeframe-select"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
          </select>
        </div>
      </div>
      
      <div className="filtered-stats">
        <p>
          <strong>Showing:</strong> {filteredSales.length} sales
          <span className="filtered-revenue">
            <strong>Earnings:</strong> ${getFilteredRevenue()}
          </span>
        </p>
      </div>
      
      {filteredSales.length === 0 ? (
        <div className="no-sales-message">
          <h3>No sales found</h3>
          <p>
            {salesData.length === 0 
              ? "You haven't made any sales yet. Create compelling listings to attract customers!"
              : "No sales match your current filters. Try adjusting your search or timeframe."}
          </p>
          <Link to="/add-listing" className="action-button">
            Create New Listing
          </Link>
        </div>
      ) : (
        <div className="sales-table-container">
          <table className="sales-table">
            <thead>
              <tr>
                <th className={`sortable ${sortField === 'listingTitle' ? 'active-sort' : ''}`} onClick={() => handleSort('listingTitle')}>
                  Listing
                  {sortField === 'listingTitle' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className={`sortable ${sortField === 'purchaserName' ? 'active-sort' : ''}`} onClick={() => handleSort('purchaserName')}>
                  Customer
                  {sortField === 'purchaserName' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className={`sortable ${sortField === 'purchaseDate' ? 'active-sort' : ''}`} onClick={() => handleSort('purchaseDate')}>
                  Date
                  {sortField === 'purchaseDate' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th>Total</th>
                <th>Platform Fee</th>
                <th className={`sortable ${sortField === 'providerAmount' ? 'active-sort' : ''}`} onClick={() => handleSort('providerAmount')}>
                  Your Earnings
                  {sortField === 'providerAmount' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                    <th>Qty</th>
                    <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((sale, index) => (
                <tr key={sale.paymentId || index} className={sale.isDeleted ? 'deleted-listing-row' : ''}>
                  <td className="listing-cell">
                    <div className="listing-info">
                      {!sale.isDeleted && sale.listingImage && (
                        <div className="listing-thumbnail">
                          <img src={sale.listingImage} alt={sale.listingTitle} />
                        </div>
                      )}
                      {sale.isDeleted && (
                        <div className="listing-thumbnail deleted-thumbnail">
                          <span className="deleted-icon">✕</span>
                        </div>
                      )}
                      <div className="listing-details">
                        <div className="listing-title">
                          {sale.listingTitle}
                          {sale.isDeleted && <span className="deleted-tag">Deleted</span>}
                        </div>
                        <div className={`listing-category ${sale.isDeleted ? 'deleted' : ''}`}>
                          {sale.isDeleted ? 'Unavailable' : sale.category}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="customer-cell">
                    <div className="customer-name">{sale.purchaserName}</div>
                    <div className="customer-email">{sale.purchaserEmail}</div>
                  </td>
                  <td className="date-cell">
                    <div className="purchase-date">{formatDate(sale.purchaseDate)}</div>
                    <div className="purchase-time">{formatTime(sale.purchaseDate)}</div>
                  </td>
                  <td className="amount-cell">
                    ${sale.totalAmount?.toFixed(2) || '0.00'}
                  </td>
                  <td className="amount-cell">
                    ${sale.platformFee?.toFixed(2) || '0.00'}
                  </td>
                  <td className="amount-cell earnings-cell">
                    ${sale.providerAmount?.toFixed(2) || '0.00'}
                  </td>
                  <td className="amount-cell">{sale.quantity || 1}</td>
                  <td className="actions-cell">
                    {!sale.isDeleted && (
                      <button
                        className="view-members-btn"
                        onClick={() => showMembersForListing(sale.listingId, sale.listingTitle)}
                        title="View all members who signed up for this listing"
                      >
                        View Members ({getMemberCountForListing(sale.listingId)})
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="dashboard-footer">
        <p>Need to add more listings? <Link to="/add-listing" className="dashboard-link">Create a new listing</Link></p>
        {stripeConnectStatus && stripeConnectStatus.onboardingStatus === 'completed' && (
          <p>Payment settings: <button onClick={() => setShowStripeConnect(true)} className="dashboard-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Manage Stripe Account</button></p>
        )}
        <p>Provider resources: <a href="https://www.canva.com/design/DAFfDyc8Vdc/YgMlfYT1yxPYwiIcCbIMww/edit?utm_content=DAFfDyc8Vdc&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton" target="_blank" rel="noopener noreferrer" className="dashboard-link">View Provider Handbook</a></p>
      </div>

      {/* Members List Modal */}
      {showMembersList && selectedListingForMembers && (
        <div className="members-modal-overlay">
          <div className="members-modal">
            <div className="members-modal-header">
              <h3>Members for "{selectedListingForMembers.title}"</h3>
              <button className="close-modal-btn" onClick={closeMembersList}>×</button>
            </div>
            
            <div className="members-modal-content">
              {getMembersForListing(selectedListingForMembers.id).length === 0 ? (
                <div className="no-members-message">
                  <p>No members have signed up for this listing yet.</p>
                </div>
              ) : (
                <div className="members-table-container">
                  <table className="members-table">
                    <thead>
                      <tr>
                        <th>Member Name</th>
                        <th>Email</th>
                        <th>Purchase Date</th>
                        <th>Qty</th>
                        <th>Amount Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getMembersForListing(selectedListingForMembers.id).map((member) => (
                        <tr key={member.id}>
                          <td className="member-name">{member.name}</td>
                          <td className="member-email">{member.email}</td>
                          <td className="member-date">
                            <div>{formatDate(member.purchaseDate)}</div>
                            <div className="member-time">{formatTime(member.purchaseDate)}</div>
                          </td>
                          <td className="member-qty">{member.quantity || 1}</td>
                          <td className="member-amount">${member.amount?.toFixed(2) || '0.00'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            <div className="members-modal-footer">
              <p><strong>Total Members:</strong> {getMembersForListing(selectedListingForMembers.id).length}</p>
              <button className="close-modal-btn-footer" onClick={closeMembersList}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceProviderDashboard;
